import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { createContext, useContext, useMemo, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { useDevice } from '../../providers/Device';
import { useIsInBottomSheet } from '../../providers/IsInBottomSheetProvider';
import { getTestIds } from '../../utils/TestIds';
import { BottomSheet, BottomSheetContentLayout } from '../BottomSheet';
import { Column, Row } from '../Layout';
import { MobileSummaryCard } from '../TextField/MobileSummaryCard';
import { Typography } from '../Typography';
import styles from './EditableFieldGroup.module.scss';
const IsInEditSheetContext = /*#__PURE__*/ createContext(false);
export const GetEditableFieldGroupTID = (parentTestId)=>getTestIds(parentTestId);
export function EditableFieldGroupInlineOnly(param) {
    let { children } = param;
    const isInEditSheet = useContext(IsInEditSheetContext);
    return isInEditSheet ? null : /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
}
export function EditableFieldGroupRow(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    const isInEditSheet = useContext(IsInEditSheetContext);
    return isInEditSheet ? /*#__PURE__*/ _jsx(Column, _object_spread_props(_object_spread({}, props), {
        alignItems: "stretch",
        wrap: false,
        className: cn(styles.stackedRow, className)
    })) : /*#__PURE__*/ _jsx(Row, _object_spread_props(_object_spread({}, props), {
        className: className
    }));
}
export function EditableFieldGroup(param) {
    let { summary, children, saveLabel, editLabel, removeLabel, onEditStart, onSave, onDiscard, onRemove, openOnMount, error, disabled, bi, testId } = param;
    const { EditableFieldGroupTestIds } = useMemo(()=>GetEditableFieldGroupTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const [isEditing, setIsEditing] = useState(!!openOnMount && !disabled);
    if (!isMobileDevice || isInBottomSheet) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    const startEditing = ()=>{
        onEditStart === null || onEditStart === void 0 ? void 0 : onEditStart();
        setIsEditing(true);
    };
    const discardEditing = ()=>{
        setIsEditing(false);
        onDiscard === null || onDiscard === void 0 ? void 0 : onDiscard();
    };
    const save = async ()=>{
        if (await onSave()) {
            setIsEditing(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(MobileSummaryCard, {
                testId: EditableFieldGroupTestIds.Card(),
                error: error,
                items: [
                    {
                        body: /*#__PURE__*/ _jsx(Column, {
                            gap: 8,
                            children: summary.map((param)=>{
                                let { label, value } = param;
                                return /*#__PURE__*/ _jsxs(Column, {
                                    gap: 4,
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            skin: "secondary",
                                            children: label
                                        }),
                                        /*#__PURE__*/ _jsx(Typography, {
                                            ellipsis: true,
                                            children: value
                                        })
                                    ]
                                }, label);
                            })
                        }),
                        actions: compact([
                            !disabled && {
                                type: 'edit',
                                testId: EditableFieldGroupTestIds.EditButton(),
                                ariaLabel: editLabel,
                                onClick: startEditing,
                                bi: bi.edit
                            },
                            !disabled && {
                                type: 'remove',
                                testId: EditableFieldGroupTestIds.RemoveButton(),
                                ariaLabel: removeLabel,
                                onClick: onRemove,
                                bi: bi.remove
                            }
                        ])
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: isEditing,
                onClose: discardEditing,
                height: "content",
                closeSheetOnLeave: true,
                children: /*#__PURE__*/ _jsx(IsInEditSheetContext.Provider, {
                    value: true,
                    children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        contentGutter: "m",
                        contentPaddingTop: 8,
                        footer: {
                            paddingTop: 16,
                            primaryAction: {
                                text: saveLabel,
                                onClick: save,
                                testId: EditableFieldGroupTestIds.SaveButton(),
                                bi: bi.save
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Column, {
                            gap: 16,
                            children: children
                        })
                    })
                })
            })
        ]
    });
}
